/**
 * Central registry of every valid `screens` permission key.
 *
 * A `customPermissions.screens` entry (or a role's base `permissions.screens`)
 * is one of these strings. Each is checked identically at two generic call
 * sites — `menuItemIsDisabled` (src/components/shared/menu/MenuComponent.tsx,
 * hides the nav item) and `ScreenPermissionGate` (src/layout/private-routes/
 * ScreenPermissionGate.tsx, blocks direct URL navigation) — both via
 * `Permissions.checkAbility(ability, Action.VIEW, key, { facilityId })` from
 * `@liveline/ll_node_common`. For page-level keys, the key string is also the
 * route path itself (`ScreenPermissionGate` derives it directly from
 * `location.pathname`).
 *
 * This file exists so anyone editing `custom_permissions.screens` by hand (or
 * via the admin UI once rebuilt — see roles_vs_custom_permissions_plan.md) has
 * one place to find valid keys, instead of grepping component files for
 * string literals. Adding a new page here does not create a route or a gate
 * by itself — it documents one that already exists (or, for a new one, must
 * be paired with the actual route/component work).
 */
export const ScreenKey = {
    // Top-level pages with no parent group
    DASHBOARD: 'dashboard',
    OEE_CONSOLE: 'oee-console',
    MATERIAL_TRACKING: 'material_tracking',
    // LT-3712 - Material Tracking is a group with two sub-items. Neither is a grant of its own (yet): both ride the
    // material_tracking grant through SCREEN_PERMISSION_ALIAS below, so no role or user data changes. A Scan-only role
    // would need these to become real grants first.
    MATERIAL_TRACKING_SCAN: 'material_tracking/scan',
    MATERIAL_TRACKING_MANAGE: 'material_tracking/manage',

    // general_admin group
    FACILITIES: 'general_admin/facilities',
    CELLS: 'general_admin/cells',
    // LT-3589 — LivelineControls-only, like DIAGNOSTICS: aliased onto CELL_CONTROLLER_TUNING below. The
    // screen changes what a running line reads and acts on (sensor presence, machine limits, chart
    // tolerance, the OPC tag provider path), and console_server gates every write it makes the same way.
    // `/general_admin/equipment/add` rides it too: ScreenPermissionGate keys on the first two segments.
    EQUIPMENT: 'general_admin/equipment',
    PRODUCTS: 'general_admin/products',
    USERS: 'general_admin/users',
    CELL_LAYOUT: 'general_admin/cell-layout',

    // live_historical_data group (note: two of its pages use a live_data prefix)
    SIGNAL_LAYOUT: 'live_data/scada1',
    SIGNAL_DATA: 'live_data/scada2',
    HISTORICAL_RUNS: 'live_historical_data/historical_runs',
    // LT-3202 — product trend and comparison reports. Neither is a grant of its own (yet): both ride the
    // Historical Runs grant through SCREEN_PERMISSION_ALIAS below, because a new key shows to nobody until
    // roles carry it and screen grants have no write path from the UI. Under live_historical_data so the
    // Monitor group opens on them without a navTree mapping.
    TREND_REPORTS: 'live_historical_data/trend_reports',
    COMPARE_PRODUCTS: 'live_historical_data/compare_products',
    ADVANCED_CONTROLLERS: 'live_historical_data/advanced_controllers',
    VIRTUAL_SENSOR: 'live_historical_data/virtual_sensor',
    LIVE_SERVICES: 'live_historical_data/live_services',

    // controls_admin group
    RULES_CONFIGURATION: 'controls_admin/rules_configuration',
    ALERTS_CONFIGURATION: 'controls_admin/alerts_configuration',
    PROCESS_INSIGHT: 'controls_admin/process_insight',
    STATE_ACTION_MATRIX: 'controls_admin/state_action_matrix',
    CELL_CONTROLLER_TUNING: 'controls_admin/cell_controller_tuning',
    // LT-3636 — the stored ProcessInsight templates and their per-facility overrides. What a template
    // says is what the controller will one day obey (phase 4 of LT-2276), so this is LivelineControls-
    // only like DIAGNOSTICS and EQUIPMENT: aliased onto CELL_CONTROLLER_TUNING below, never a grant of
    // its own, and console_server gates every write it makes the same way (LT-3635).
    PROCESS_INSIGHT_TEMPLATES: 'controls_admin/process_insight_templates',

    // tools group — offline calculators, visible to everyone (ALWAYS_VISIBLE_SCREENS).
    // PROPORTIONAL_CONTROLLER_CALCULATOR moved here from
    // controls_admin/proportional_controller_tuning; nothing outside this repo
    // referenced that key (checked all six workspace repos), so the rename is
    // front-only — unlike CELL_CONTROLLER_TUNING, which console_server hardcodes
    // as its rc3 write gate and must never move.
    PROPORTIONAL_CONTROLLER_CALCULATOR: 'tools/proportional_controller_calculator',
    RAMP_LOSS_CALCULATOR: 'tools/ramp_loss_calculator',
    // LT-3348 — Liveline Diagnostics. Lives in the tools group but is NOT in ALWAYS_VISIBLE_SCREENS.
    // It is NOT its own grant either: it is gated on CELL_CONTROLLER_TUNING via SCREEN_PERMISSION_ALIAS
    // below — "LivelineControls-only" in this codebase means "holds controls_admin/cell_controller_tuning"
    // (console_server hardcodes that key as the LivelineControls write lock), so anyone who can open the
    // tuning page can open this one with no DB change. Two segments, because ScreenPermissionGate derives
    // the key from the first two path segments.
    DIAGNOSTICS: 'tools/diagnostics',
    // LT-3578 — the Signal Reader: the OPC reader (and, from LT-3392, the Kafka reader), moved off
    // Diagnostics so that page stays a verdict. Same gate, same mechanism: SCREEN_PERMISSION_ALIAS
    // below, never a grant of its own (screens have no working grant path).
    SIGNAL_READER: 'tools/signal-reader',

    // ml group
    ML_LPP: 'ml/lpp',
    ML_LCP: 'ml/lcp',
    ML_BACKTEST: 'ml/backtest',
} as const;

export type ScreenKeyValue = (typeof ScreenKey)[keyof typeof ScreenKey];

/**
 * Screens whose permission check is ANOTHER screen's key. Resolved in the two generic gate sites
 * (`menuItemIsDisabled` and `ScreenPermissionGate`) so nav filter, facility-switch redirects and direct
 * URL navigation all agree. Use this — not a bespoke check — when a page should ride an existing grant.
 */
export const SCREEN_PERMISSION_ALIAS: Readonly<Record<string, string>> = {
    [ScreenKey.DIAGNOSTICS]: ScreenKey.CELL_CONTROLLER_TUNING,
    [ScreenKey.EQUIPMENT]: ScreenKey.CELL_CONTROLLER_TUNING,
    [ScreenKey.SIGNAL_READER]: ScreenKey.CELL_CONTROLLER_TUNING,
    [ScreenKey.PROCESS_INSIGHT_TEMPLATES]: ScreenKey.CELL_CONTROLLER_TUNING,
    [ScreenKey.TREND_REPORTS]: ScreenKey.HISTORICAL_RUNS,
    [ScreenKey.COMPARE_PRODUCTS]: ScreenKey.HISTORICAL_RUNS,
    [ScreenKey.MATERIAL_TRACKING_SCAN]: ScreenKey.MATERIAL_TRACKING,
    [ScreenKey.MATERIAL_TRACKING_MANAGE]: ScreenKey.MATERIAL_TRACKING,
};

/**
 * Groups the Users permission grid shows as ONE row, under the group's own key, because their children only ride the
 * group's grant (see SCREEN_PERMISSION_ALIAS). Without this the grid would list the children as rows that grant nothing
 * and drop the material_tracking row that does.
 */
export const GROUPS_GRANTED_AS_ONE: ReadonlySet<string> = new Set<string>([ScreenKey.MATERIAL_TRACKING]);

// LT-3810: a screen may also be opened by a facility ROLE beside its grant — SCREEN_ROLE_ALLOW, in
// facilityRoles.ts, checked at the same two gate sites as SCREEN_PERMISSION_ALIAS.

/** The key `checkAbility` should be asked about for a given screen key or route-derived key. */
export const permissionKeyFor = (screenKey: string): string => SCREEN_PERMISSION_ALIAS[screenKey] ?? screenKey;

/** The path `controls_admin/proportional_controller_tuning` used to live at. */
export const LEGACY_PROPORTIONAL_CONTROLLER_PATH = '/controls_admin/proportional_controller_tuning';

/**
 * Screens every user can see, regardless of role.
 *
 * Both are offline calculators: pure client-side arithmetic over slider inputs,
 * no cell, no facility, no API. There is deliberately no grant for them, and
 * there could not usefully be one — roles are read-only through the whole stack
 * (console_server's roles router is GET-only), the admin screens grid is dead
 * code, and the live Edit User form overwrites `custom_permissions.screens` with
 * `[]` on every save. Gating these on a grant would ship them unreachable.
 *
 * The waiver lives inside `menuItemIsDisabled` so all four of its call sites —
 * the nav filter, the drawer filter and BOTH facility-switch redirects — are
 * correct at once; `ScreenPermissionGate` mirrors it for direct URL navigation.
 */
export const ALWAYS_VISIBLE_SCREENS: ReadonlySet<string> = new Set<string>([
    ScreenKey.PROPORTIONAL_CONTROLLER_CALCULATOR,
    ScreenKey.RAMP_LOSS_CALCULATOR,
]);
